<template>
  <tr @click="expanded = !expanded">
    <td class="width-sku">
      <router-link
        :to="{
          name: 'single-product',
          params: { id: product.id }
        }"
        tabindex="-1"
      >
        {{ product.sku }}
      </router-link>
    </td>
    <td class="width-order">
      {{ product.order }}
    </td>
    <td class="width-name">
      {{ product.name }}
    </td>
    <template v-if="!product.children || !product.children.length">
      <td class="width-qty">{{ product.inventory.quantity }}</td>
      <td class="width-qty-input">
        <v-text-field
          v-model.number="quantity"
          outline
          flat
          hide-details
          solo
          class="my-3"
          type="number"
          @keypress.enter="updateQuantity"
        />
      </td>
      <td class="width-btn-adjust ">
        <v-layout>
          <v-btn
            :loading="loading"
            color="primary"
            tabindex="-1"
            @click="updateQuantity"
          >
            {{ $t('Adjust') }}
          </v-btn>
          <v-flex>
            <v-tooltip
              v-if="((diff > 0 && !isRestockable) || (diff < 0 && !isDeductible)) && quantity"
              bottom
            >
              <template v-slot:activator="{ on }">
                <v-icon
                  color="primary"
                  dark
                  v-on="on"
                >
                  warning
                </v-icon>
              </template>
              <span v-if="diff < 0 && !isDeductible">{{ $t(deductibleWarning) }}</span>
              <span v-else-if="diff > 0 && !isRestockable">{{ $t(restockableWarning) }}</span>
            </v-tooltip>
          </v-flex>
        </v-layout>
      </td>
    </template>
    <template v-else>
      <td class="width-qty"/>
      <td class="width-qty-input"/>
      <td class="width-btn-adjust">
        <v-flex v-if="product.children.filter(i => i.inventory.track).length > 0">
          <v-btn
            flat
            tabindex="-1"
          >
            <v-icon>{{ expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
          </v-btn>
        </v-flex>
        <v-flex v-else>
          {{ $t('No inventoriable variants') }}
        </v-flex>
      </td>
    </template>
  </tr>
</template>

<script>
export default {
  name: 'ProductInventoryRow',

  props: {
    item: {
      type: Object,
      required: true
    },
    parent: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Boolean,
      default: false
    },
    isChild: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      restockableWarning: 'Quantity won\'t change when product inventory is not restockable and/or not tracked',
      deductibleWarning: 'Quantity won\'t change when product inventory is not tracked',
      product: { ...this.item },
      loading: false,
      quantity: ''
    }
  },

  computed: {
    expanded: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    diff () {
      return this.quantity - this.product.inventory.quantity
    },

    isDeductible () {
      return this.product.inventory.track
    },
    isRestockable () {
      return this.product.inventory.is_restockable && this.product.inventory.track
    }
  },

  methods: {
    async updateQuantity () {
      if (this.quantity === '') {
        return
      }

      this.loading = true

      const product = await this.$http.make('product').find(this.product.id)
      product.quantity = this.quantity

      if (this.diff > 0 && !this.isRestockable) {
        this.$toast.warning(this.$t(this.restockableWarning))
        this.loading = false
        return
      }

      if (this.diff < 0 && !this.isDeductible) {
        this.$toast.warning(this.$t(this.deductibleWarning))
        this.loading = false
        return
      }

      try {
        if (this.diff > 0) {
          await product.restock(this.diff)
        } else {
          await product.deduct(this.product.inventory.quantity - this.quantity)
        }
        this.product = product
        this.quantity = ''
        this.$toast.success(this.$t('Quantity updated'))
      } catch (error) {
        this.$toast.error(this.$t(`Unable to update quantity for ${this.item.name}`))
        this.$toast.error(this.$t(error.message))
      } finally {
        this.loading = false
        if (this.isChild) {
          this.$emit('updateParent', this.parent, product)
        }
      }
    }
  }
}
</script>

<style scoped>
.width-order {
  width: 10% !important;
}

.width-sku {
  width: 25% !important;
}
.width-name {
  width: 25% !important;
}
.width-qty {
  width: 10% !important;
}
.width-qty-input {
  width: 15% !important;
}
.width-btn-adjust {
  width: 15% !important;
}

</style>
